import { useState, useEffect, useCallback, useMemo } from 'react';
import sanitizeText from '@/utils/sanitizeText';
import { useAuth } from '@/context/AuthContext';
import {
  FaBook,
  FaPlus,
  FaEdit,
  FaTrash,
  FaStar,
  FaTag,
  FaGlobe,
  FaInfoCircle,
  FaSync,
  FaTimes,
  FaCheck,
  FaSearch,
  FaFilter,
  FaToggleOn,
  FaToggleOff,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';
import './RouterKnowledgeBase.scss';
import * as Sentry from '@sentry/react';

const KNOWLEDGE_TYPES = [
  'Note',
  'Pattern',
  'Issue',
  'Standard',
  'Security',
  'NetworkTopology',
  'BestPractice'
];

/**
 * RouterKnowledgeBase Component
 * Manages knowledge base entries for router configurations
 */
const RouterKnowledgeBase = ({ routerId }) => {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [searchTags, setSearchTags] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    type: 'Note',
    content: '',
    tags: '',
    priority: 3,
    configExample: '',
    relatedIssue: '',
    externalReference: '',
    isActive: true
  });

  /**
   * Load all knowledge entries for this router
   */
  const loadKnowledgeEntries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/knowledge`);

      if (!response.ok) {
        throw new Error('Failed to load knowledge entries');
      }

      const data = await response.json();
      if (data.success) {
        setEntries(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load knowledge entries');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading knowledge entries:', err);
      setError(err.message || 'Failed to load knowledge entries');
    } finally {
      setLoading(false);
    }
  }, [routerId, apiFetch]);

  /**
   * Apply filters to entries — derived state, no effect needed
   */
  const filteredEntries = useMemo(() => {
    let filtered = [...entries];
    if (filterType) {
      filtered = filtered.filter(e => e.type === filterType);
    }
    if (searchTags) {
      const searchTagsLower = searchTags.toLowerCase();
      filtered = filtered.filter(e =>
        e.tags && e.tags.toLowerCase().includes(searchTagsLower)
      );
    }
    return filtered;
  }, [entries, filterType, searchTags]);

  useEffect(() => {
    if (routerId) {
      (async () => { await loadKnowledgeEntries(); })();
    }
  }, [routerId, loadKnowledgeEntries]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  /**
   * Open modal for adding new entry
   */
  const handleAddNew = () => {
    setEditingEntry(null);
    setFormData({
      title: '',
      type: 'Note',
      content: '',
      tags: '',
      priority: 3,
      configExample: '',
      relatedIssue: '',
      externalReference: '',
      isActive: true
    });
    setShowModal(true);
  };

  /**
   * Open modal for editing entry
   */
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      type: entry.type || 'Note',
      content: entry.content || '',
      tags: entry.tags || '',
      priority: entry.priority || 3,
      configExample: entry.configExample || '',
      relatedIssue: entry.relatedIssue || '',
      externalReference: entry.externalReference || '',
      isActive: entry.isActive !== false
    });
    setShowModal(true);
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Save entry (create or update)
   */
  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...formData,
        priority: parseInt(formData.priority, 10)
      };

      let response;
      if (editingEntry) {
        // Update existing
        response = await apiFetch(`/api/RouterConfig/knowledge/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new
        response = await apiFetch(`/api/RouterConfig/${routerId}/knowledge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        throw new Error(editingEntry ? 'Failed to update entry' : 'Failed to create entry');
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(editingEntry ? 'Entry updated successfully' : 'Entry created successfully');
        setShowModal(false);
        await loadKnowledgeEntries();
      } else {
        throw new Error(data.error || (editingEntry ? 'Failed to update entry' : 'Failed to create entry'));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving entry:', err);
      setError(err.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Delete entry with confirmation
   */
  const handleDelete = async (entryId) => {
    if (!confirm('Are you sure you want to delete this knowledge entry?')) {
      return;
    }

    setDeleting(entryId);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/knowledge/${entryId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }

      const data = await response.json();
      if (data.success) {
        setSuccess('Entry deleted successfully');
        await loadKnowledgeEntries();
      } else {
        throw new Error(data.error || 'Failed to delete entry');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error deleting entry:', err);
      setError(err.message || 'Failed to delete entry');
    } finally {
      setDeleting(null);
    }
  };

  /**
   * Toggle entry active status
   */
  const handleToggleActive = async (entry) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...entry,
        isActive: !entry.isActive
      };

      const response = await apiFetch(`/api/RouterConfig/knowledge/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to toggle entry status');
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(`Entry ${entry.isActive ? 'deactivated' : 'activated'} successfully`);
        await loadKnowledgeEntries();
      } else {
        throw new Error(data.error || 'Failed to toggle entry status');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error toggling entry status:', err);
      setError(err.message || 'Failed to toggle entry status');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Get type color class
   */
  const getTypeColor = (type) => {
    const colors = {
      Note: 'type-note',
      Pattern: 'type-pattern',
      Issue: 'type-issue',
      Standard: 'type-standard',
      Security: 'type-security',
      NetworkTopology: 'type-network',
      BestPractice: 'type-best-practice'
    };
    return colors[type] || 'type-default';
  };

  /**
   * Render priority stars
   */
  const renderPriority = (priority) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={i <= priority ? 'star-filled' : 'star-empty'}
        />
      );
    }
    return <div className="priority-stars">{stars}</div>;
  };

  if (loading) {
    return (
      <div className="router-knowledge-base">
        <div className="loading-message">
          <FaSync className="fa-spin" />
          <span>Loading knowledge base...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="router-knowledge-base">
      <div className="knowledge-header">
        <h3>
          <FaBook /> Router Knowledge Base
        </h3>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={loadKnowledgeEntries}
            disabled={loading}
          >
            <FaSync /> Refresh
          </button>
          <button className="btn btn-success" onClick={handleAddNew}>
            <FaPlus /> Add Entry
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <FaExclamationTriangle /> {sanitizeText(error)}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FaCheckCircle /> {sanitizeText(success)}
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="filter-search-bar">
        <div className="filter-group">
          <FaFilter />
          <select
            className="form-control"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {KNOWLEDGE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="search-group">
          <FaSearch />
          <input
            type="text"
            className="form-control"
            placeholder="Search by tags..."
            value={searchTags}
            onChange={(e) => setSearchTags(e.target.value)}
          />
        </div>
      </div>

      {/* Entries List */}
      <div className="entries-list">
        {filteredEntries.length === 0 ? (
          <div className="no-entries">
            <FaBook />
            <p>
              {entries.length === 0
                ? 'No knowledge entries found. Add your first entry above.'
                : 'No entries match your filters.'}
            </p>
          </div>
        ) : (
          <div className="entries-grid">
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                className={`entry-card ${!entry.isActive ? 'inactive' : ''}`}
              >
                <div className="entry-header">
                  <div className="entry-title-row">
                    <h4>{sanitizeText(entry.title)}</h4>
                    <div className="entry-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleActive(entry)}
                        title={entry.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {entry.isActive ? (
                          <FaToggleOn className="toggle-on" />
                        ) : (
                          <FaToggleOff className="toggle-off" />
                        )}
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEdit(entry)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(entry.id)}
                        disabled={deleting === entry.id}
                        title="Delete"
                      >
                        {deleting === entry.id ? (
                          <FaSync className="fa-spin" />
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="entry-meta">
                    <span className={`type-badge ${getTypeColor(entry.type)}`}>
                      {sanitizeText(entry.type)}
                    </span>
                    {renderPriority(entry.priority)}
                  </div>
                </div>

                <div className="entry-content">
                  <p>{sanitizeText(entry.content)}</p>
                </div>

                {entry.tags && (
                  <div className="entry-tags">
                    <FaTag />
                    {entry.tags.split(',').map((tag, idx) => (
                      <span key={idx} className="tag">
                        {sanitizeText(tag.trim())}
                      </span>
                    ))}
                  </div>
                )}

                {entry.configExample && (
                  <div className="entry-config">
                    <FaInfoCircle />
                    <span>Has config example</span>
                  </div>
                )}

                {entry.externalReference && (
                  <div className="entry-external">
                    <FaGlobe />
                    <a
                      href={entry.externalReference}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      External Reference
                    </a>
                  </div>
                )}

                <div className="entry-stats">
                  <span>Used: {entry.usageCount || 0} times</span>
                  {entry.lastUsedDate && (
                    <span>
                      Last: {new Date(entry.lastUsedDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingEntry ? 'Edit Knowledge Entry' : 'Add Knowledge Entry'}
              </h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter title"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Type <span className="required">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    {KNOWLEDGE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    className="form-control"
                    value={formData.priority}
                    onChange={(e) =>
                      handleInputChange('priority', parseInt(e.target.value, 10))
                    }
                  >
                    {[1, 2, 3, 4, 5].map((p) => (
                      <option key={p} value={p}>
                        {p} Star{p > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Content <span className="required">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Enter content"
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder="Comma-separated tags (e.g., firewall, nat, security)"
                />
              </div>

              <div className="form-group">
                <label>Config Example</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.configExample}
                  onChange={(e) =>
                    handleInputChange('configExample', e.target.value)
                  }
                  placeholder="Optional configuration example"
                />
              </div>

              <div className="form-group">
                <label>Related Issue</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.relatedIssue}
                  onChange={(e) =>
                    handleInputChange('relatedIssue', e.target.value)
                  }
                  placeholder="Related issue ID or reference"
                />
              </div>

              <div className="form-group">
                <label>External Reference</label>
                <input
                  type="url"
                  className="form-control"
                  value={formData.externalReference}
                  onChange={(e) =>
                    handleInputChange('externalReference', e.target.value)
                  }
                  placeholder="https://example.com/docs"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange('isActive', e.target.checked)
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSync className="fa-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <FaCheck /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouterKnowledgeBase;
